import { MapSearchIcon } from '@/components/atoms/customIcons/mapSearchIcon';
import { SearchMunicipalityForm } from '@/components/organisms/searchMunicipality/form';
import { ChevronLeft, FilterList, ViewListOutlined } from '@mui/icons-material';
import { AppBar as MuiAppBar, AppBarProps as MuiAppBarProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { SyntheticEvent, RefObject } from 'react';
import { StyledDrawer, StyledIconButton, StyledTab, StyledTabs, StyledToolbar } from './styles';
import { getDrawerWidth } from '../styles';
import { useTranslation } from '@/hooks/useTranslation';

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ open }: { open: boolean }) => open,
      style: ({ open }: { open: boolean }) => ({
        paddingLeft: `${getDrawerWidth(open)}px`,
        transition: theme.transitions.create(['margin', 'width'], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }),
    },
  ],
}));

export const DrawerHeader = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: 'flex-end',
}));

interface MenuProps {
  value: number;
  open: boolean;
  className?: string;
  id?: string;
  ref?: RefObject<HTMLDivElement | null>;
  handleChange: (event: SyntheticEvent, newValue: number) => void;
  toggleDrawer: () => void;
  toggleAdvancedSearch?: () => void;
  hasMunicipalityData?: boolean;
  onResetMunicipalityData?: () => void;
}

export const Menu = ({
  value,
  open,
  className,
  id,
  ref,
  handleChange,
  toggleDrawer,
  toggleAdvancedSearch,
}: MenuProps) => {
  const { t } = useTranslation();

  return (
    <>
      <AppBar
        position="absolute"
        open={open}
        sx={{ background: 'transparent', boxShadow: 'none', zIndex: 900 }}
      >
        <StyledToolbar open={open}>
          <StyledIconButton
            color="inherit"
            aria-label="open drawer"
            onClick={toggleDrawer}
            sx={[open && { display: 'none' }]}
          >
            <FilterList color="inherit" sx={{ height: 24, width: 24 }} />
          </StyledIconButton>

          <StyledIconButton onClick={toggleDrawer} sx={[!open && { display: 'none' }]}>
            <ChevronLeft color="inherit" sx={{ height: 24, width: 24 }} />
          </StyledIconButton>
        </StyledToolbar>
      </AppBar>

      <StyledDrawer variant="persistent" anchor="left" open={open}>
        <DrawerHeader sx={{ padding: 0 }} id={id} ref={ref} className={className}>
          <StyledTabs value={value} onChange={handleChange}>
            <StyledTab
              label={t('tabs.tabs.table.interactive_map')}
              icon={<MapSearchIcon />}
              iconPosition="start"
            />

            <StyledTab
              label={t('tabs.tabs.table.table')}
              icon={<ViewListOutlined />}
              iconPosition="start"
            />
          </StyledTabs>
        </DrawerHeader>

        <SearchMunicipalityForm toggleAdvancedSearch={toggleAdvancedSearch} />
      </StyledDrawer>
    </>
  );
};
